<script setup>
import { ref, toRef, defineProps, defineEmits, watch } from 'vue';
import axios from 'axios';
import VueElementLoading from 'vue-element-loading';
import draggable from "vuedraggable";
import ImgEditModal from './ImgEditModal.vue';
import PencilSVG from '../SVG/PencilSVG.vue';
import TriangleAlertSVG from '../SVG/TriangleAlertSVG.vue';

const props = defineProps(['baseUrl', 'token', 'initImages']);
const emits = defineEmits(['handleUpload']);

const imagesData = ref([]);
const currentImgData = ref(null);
const baseUrl = toRef(props, 'baseUrl');
const token = toRef(props, 'token');
const initImages = toRef(props, 'initImages');
const imgRef = ref(null);
const isLoading = ref(false);
const isEditModalOpen = ref(false);

const DataURIToBlob = (dataURI) => {
  const splitDataURI = dataURI.split(',')
  const byteString = splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1])
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0]

  const ia = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++)
    ia[i] = byteString.charCodeAt(i)

  return new Blob([ia], {
    type: mimeString
  })
}

const triggerFileUpload = () => {
  imgRef.value.click();
}

const updateImagesData = (val) => {
  if (val?.uuid) {
    const index = imagesData.value.findIndex((item) => item.uuid === val.uuid);
    if (index === -1) {
      imagesData.value.push(val);
    } else {
      imagesData.value[index] = val;
    }
  }
}

const handleImgUpload = async (file, size) => {
  return new Promise((resolve, reject) => {
    (async () => {
      isLoading.value = true;
      try {
        const res = await axios.post(`${baseUrl.value}/v2/private/image-files`, 
          { file, size },
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token.value}`
            }
          }
        );
        currentImgData.value = res.data;
        handleUpload();
        resolve();
      } catch(err) {
        console.log('error-->>', err);
        reject(err);
      } finally {
        isLoading.value = false;
      }
    })();
  });
}

const handleImgselect = async (e) => {
  try {
    closeEditModal();
    if (e.target.files.length === 0) return;
    const file = e.target.files[0];
    await handleImgUpload(file, "full");
    openEditModal();
  } catch(err) {
    console.log('error-->>', err);
  }
}

const handleEditImg = async (payload) => {
  const cardImg = DataURIToBlob(payload.card);
  const thumbImg = DataURIToBlob(payload.thumb);

  try {
    isLoading.value = true;

    const uploadImage = (file, size) => {
      return axios.post(`${baseUrl.value}/v2/private/image-files`, 
        { file, size, id: currentImgData.value.uuid },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token.value}`
          }
        }
      );
    };

    const cardImgRes = await uploadImage(cardImg, "card");
    currentImgData.value = cardImgRes.data;
    const thumbImgRes = await uploadImage(thumbImg, "thumb");
    currentImgData.value = thumbImgRes.data;
    updateImagesData(cardImgRes.data);
    updateImagesData(thumbImgRes.data);
    console.log(cardImgRes, 'thumbImgRes-->>', thumbImgRes);
    handleUpload();
  } catch(err) {
    console.log('error-->>', err);
  } finally {
    isLoading.value = false;
    closeEditModal();
  }
}

const handleRemoveImg = (uuid) => {
  const index = imagesData.value.findIndex((item) => item.uuid === uuid);
  if (index !== -1) {
    imagesData.value.splice(index, 1);
  }
  closeEditModal();
}

const openEditModal = () => {
  isEditModalOpen.value = true;
}

const closeEditModal = () => {
  isEditModalOpen.value = false;
}

const handleUpload = () => {
  emits('handleUpload', imagesData.value);
}

const onEditModal = (value) => {
  currentImgData.value = value;
  openEditModal();
}

watch(initImages, (val) => {
  imagesData.value = val;
});

</script>

<template>
  <div class="d-flex flex-column gap-8px">
    <div class="image-length-text">
      Immagini {{ imagesData.length }} / 5
    </div>
    <div v-if="isLoading">
      <vue-element-loading :active="isLoading" spinner="spinner" color="#2D489D" />
    </div>
    <div v-else class="d-flex flex-wrap gap-8px">
      <draggable tag="ul" :list="imagesData" class="d-flex flex-wrap bg-white gap-16px p-0" handle=".handle" item-key="uuid">
          <template #item="{ element }">
            <li class="d-flex flex-column align-items-center gap-8px">
              <div class="home-cropped-image position-relative" @click="() => onEditModal(element)">
                <div class="crop-internal">
                  <img :src="element?.card" :alt="element.alt" />
                </div>
                <div v-if="element?.cardFallback" class="warning-badge position-absolute z-3 top-0 start-100 translate-middle badge rounded-pill bg-warning">
                  <TriangleAlertSVG />
                </div>
                <div class="home-cropped-image-overlay">
                  <p v-if="element?.cardFallback" class="z-3 fw-semibold text-secondary text-center">formato obsoleto, editare l'immagine per salvarla nel formato corretto</p>
                  <div v-else>
                    <PencilSVG />
                  </div>
                </div>
              </div>
              <div class="drag-img d-flex">
                <div class="handle"><img src="@/assets/icons/drag.png" /></div>
              </div>
            </li>
          </template>
        </draggable>
      <label v-if="imagesData.length < 5" for="image-upload">
        <div class="bg-add-image min-w-128px min-h-128px w-20 d-flex flex-column justify-content-center align-items-center">
          <img src="@/assets/icons/add.png" />
          <div class="add-image-text">Carica foto</div>
        </div>
        <input type="file" accept="image/*" id="image-upload" ref="imgRef" @change="handleImgselect" />
      </label>
    </div>
  </div>
  <img-edit-modal
    v-if="isEditModalOpen" 
    :isModalOpened="isEditModalOpen" 
    :initialData="currentImgData"
    @onCloseModal="closeEditModal" 
    @onRemoveImg="handleRemoveImg"
    @handleSubmit="handleEditImg"
    @handleNewUpload="triggerFileUpload"
  />
</template>

<style scoped>
.gap-8px {
  gap: 8px;
}

.warning-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
}

.image-length-text {
  color: var(--neutrali-grigio-scuro, #4C4C4C);
  font-family: Titillium Web;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

#image-upload {
  display: none;
}

.w-20 {
  width: 20%;
}

.bg-add-image {
  background: var(--accent-blu-light, #DFE5F6);
  cursor: pointer;
}

.modal-footer input {
  display: none;
}

.add-image-text {
  color: var(--accent-blu-accent, #2D489D);
  text-align: center;
  font-family: Titillium Web;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
}

.drag-container img {
  width: 38px !important;
  height: 38px !important;
}

.img-show-container img,
.img-container img {
  width: 128px;
  height: 128px;
  object-fit: contain;
}

.drag-img {
  gap: 12px;
}

.drag-img>div {
  cursor: pointer;
}

.drag-img img {
  width: 24px;
  height: 24px;
}

.img-container>div>div:first-child {
  flex: 0 0 25%;
  max-width: 25%;
}

.min-w-128px {
  min-width: 128px;
}

.min-h-128px {
  min-height: 128px;
}

.gap-16px {
  column-gap: 16px;
}

.h-100 {
  height: 100px;
}

.py-16px {
  padding-top: 16px;
  padding-bottom: 16px;
}

.ml-auto {
  margin-left: auto !important;
}

.font-size-24px {
  font-size: 24px;
  font-weight: 600;
  padding-left: 32px;
}

.home-cropped-image {
  position: relative;
  cursor: pointer;
}

.home-cropped-image-overlay {
  display: none;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 100;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.4);
  transition: all 0.3s;
}

.home-cropped-image:hover .crop-internal {
  opacity: 0.2;
}

.home-cropped-image:hover>.home-cropped-image-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
}

.home-cropped-image:hover>.home-cropped-image-overlay>div {
  width: 1rem;
  height: 1rem;
}
</style>